import { Reducer } from "redux";
import { produce } from "immer";

import { EMPTY_OBJECT } from "@multiweb/reactUtils";
import { ParametricReducer, RootNodeTypedObject, TypedObject } from "@multiweb/types/react";
import { DATA_SOURCE_INIT_STATE } from "../initState";
import { dataSourceLogger } from "../logger";
import {
    ICheckAllRecordsAction,
    IDataSourceConfig,
    IDataSourceMultiselect,
    IDeselectAllRecordsAction,
    ILoadDataSuccessAction,
    IRefreshDataAction,
    IRefreshDataSuccessAction,
    ISelectNextRecord,
    ISelectPreviousRecord,
    ISelectRecordAction,
    ISelectRecordsAction,
    ISetDatasourceMultiselectModeAction,
    ISetLastSelectedRecordIdAction,
    ISetRootNodes,
    IToggleCheckAllRecordsAction,
    IToggleCheckOnSelectedRecordsAction,
    IToggleSelectedRecordCheckAction,
    IUncheckAllRecordsAction,
    RecordIdKeyType,
} from "../types";
import {
    getRecordId,
    getRecordIdByDirection,
    getSelectionDirectionFromAction,
    handleCheckingRootNodeChildren,
    handleCheckingRootNodes,
    handleCheckingWithOwnerId,
} from "../utils";
import { RecordIdType } from "./types";

type SelectedRecordsReducerParams = {
    readonly recordIdKey: RecordIdKeyType; // Při načítání dat potřebuje ze záznamu vytáhnout identifikátor.
    readonly recordsIds: ReadonlyArray<RecordIdType>; // Při výběru pomocí klávesnice potřebuje znát pořadí itemů.
    readonly lastSelectedRecordId: RecordIdType; // Při výběru pomocí klávesnice potřebuje znát poslední vybraný item.
};

const selectedRecords: ParametricReducer<TypedObject<boolean>, SelectedRecordsReducerParams> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.selectedRecords,
    action,
    reducerParams,
) => {
    if (action instanceof ILoadDataSuccessAction || action instanceof IRefreshDataSuccessAction) {
        const { data } = action.payload;
        const { recordIdKey } = reducerParams;
        const loadedDataIds = data.map((record) => getRecordId(record, recordIdKey));
        return produce(state, (draft) => {
            loadedDataIds.forEach((recordId) => (draft[recordId] = false));
        });
    } else if (action instanceof IRefreshDataAction) {
        return EMPTY_OBJECT;
    } else if (action instanceof IDeselectAllRecordsAction) {
        return produce(state, (draft) => {
            Object.keys(draft).forEach((id) => {
                draft[id] = false;
            });
        });
    } else if (action instanceof ISelectRecordsAction) {
        return produce(state, (draft) => {
            const { selectedRecordsIds } = action.payload;
            Object.keys(draft).forEach((id) => {
                draft[id] = false;
            });
            selectedRecordsIds.forEach((id) => {
                draft[id] = true;
            });
        });
    } else if (action instanceof ISelectRecordAction) {
        return produce(state, (draft) => {
            const { recordId } = action.payload;
            if (draft[recordId] !== undefined) {
                draft[recordId] = true;
            }
        });
    } else if (action instanceof ISelectNextRecord || action instanceof ISelectPreviousRecord) {
        if (reducerParams.recordsIds.length === 0) {
            dataSourceLogger.error("You can not handle click on record on DataSource without loaded record.");
            return state; // Pokud nejsou načteny žádné záznamy.
        }
        const { clearOtherSelections } = action.payload;
        const { recordsIds, lastSelectedRecordId = recordsIds[0] } = reducerParams;
        const newActualRecordId = getRecordIdByDirection(
            recordsIds,
            lastSelectedRecordId,
            getSelectionDirectionFromAction(action),
        );
        if (!newActualRecordId) {
            return state; // nelze se posunout (jsem na okraji DS).
        }
        return produce(state, (draft) => {
            if (clearOtherSelections) {
                Object.keys(draft).forEach((id) => {
                    draft[id] = false;
                });
                draft[newActualRecordId] = true;
            } else {
                if (draft[newActualRecordId]) {
                    // odoznačím aktuální řádek (keypressRow).
                    if (lastSelectedRecordId) {
                        draft[lastSelectedRecordId] = false;
                    }
                } else {
                    // označím nový řádek
                    draft[newActualRecordId] = true;
                }
            }
        });
    }
    return state;
};

type CheckedRecordsReducerParams = {
    readonly recordIdKey: string; // Při načítání dat potřebuje ze záznamu vytáhnout identifikátor.
    readonly selectedRecords: TypedObject<boolean>; // Při hromadném označování potřebuje vědět, které záznamy jsou vybrány.
    readonly keepIncomingDataChecked: boolean; // Při načítání dat potřebuje vědět v jakém jsou stavu.
    readonly isCheckedAll: boolean; // Stav zaškrtnutí všech záznamů - před výpočtem.
    readonly rootNodes: RootNodeTypedObject; // Uzlové záznamy
};

const checkedRecords: ParametricReducer<TypedObject<boolean>, CheckedRecordsReducerParams> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.checkedRecords,
    action,
    reducerParams,
) => {
    if (action instanceof ILoadDataSuccessAction || action instanceof IRefreshDataSuccessAction) {
        const { data } = action.payload;
        const { recordIdKey, keepIncomingDataChecked } = reducerParams;
        const loadedDataIds = data.map((record) => getRecordId(record, recordIdKey));
        return produce(state, (draft) => {
            loadedDataIds.forEach((id) => {
                draft[id] = keepIncomingDataChecked;
            });
        });
    } else if (action instanceof IRefreshDataAction) {
        // stejne u selected i checked
        return EMPTY_OBJECT;
    } else if (action instanceof IUncheckAllRecordsAction) {
        // stejne u selected i checked
        const { rootNodes } = reducerParams;

        return produce(state, (draft) => {
            Object.keys(draft).forEach((id) => {
                draft[id] = false;

                handleCheckingRootNodeChildren(draft, rootNodes[id], false);
            });
        });
    } else if (action instanceof IToggleCheckOnSelectedRecordsAction) {
        // Pokud je alespoň jeden vybraný (selected) řádek, ale není označen (checked),
        // tak nová hodnota bude pro všechny true (označí všechny vybrané). Pokud jsou všechny
        // vybrané (selected) záznamy označeny (checked), tak je nová hodnota false.
        const { selectedRecords, rootNodes } = reducerParams;
        const selectedRecordsIds = Object.keys(selectedRecords).filter(
            (recordId) => selectedRecords[recordId],
        );
        const newCheckState = selectedRecordsIds.some((recordId) => !state[recordId]);
        return produce(state, (draft) => {
            selectedRecordsIds.forEach((id) => {
                draft[id] = newCheckState;

                handleCheckingRootNodes(draft, rootNodes, id);
            });
        });
    } else if (action instanceof ICheckAllRecordsAction) {
        const { rootNodes } = reducerParams;

        return produce(state, (draft) => {
            Object.keys(draft).forEach((id) => {
                draft[id] = true;

                handleCheckingRootNodeChildren(draft, rootNodes[id], true);
            });
        });
    } else if (action instanceof IToggleCheckAllRecordsAction) {
        const { isCheckedAll, rootNodes } = reducerParams;

        return produce(state, (draft) => {
            Object.keys(draft).forEach((id) => {
                draft[id] = !isCheckedAll;

                handleCheckingRootNodeChildren(draft, rootNodes[id], !isCheckedAll);
            });
        });
    } else if (action instanceof IToggleSelectedRecordCheckAction) {
        const { recordId, ownerId } = action.payload;
        const { rootNodes } = reducerParams;

        return produce(state, (draft) => {
            draft[recordId] = !draft[recordId];

            handleCheckingRootNodes(draft, rootNodes, recordId);

            if (ownerId) {
                handleCheckingWithOwnerId(draft, rootNodes, ownerId);
            }
        });
    }
    return state;
};

const lastSelectedRecordId: ParametricReducer<string, ReadonlyArray<RecordIdType>> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.lastSelectedRecordId,
    action,
    recordsIds,
) => {
    if (action instanceof IRefreshDataAction) {
        return "";
    } else if (action instanceof ISetDatasourceMultiselectModeAction) {
        if (!action.payload.multiselectMode) {
            return "";
        }
    } else if (action instanceof ISetLastSelectedRecordIdAction) {
        return action.payload.recordId;
    } else if (action instanceof ISelectNextRecord || action instanceof ISelectPreviousRecord) {
        // TODO: 33097 - Tento blok bude nejspíš potřeba upravit
        // Potřebuji mit až po nastaveni SelectedRecord (tam je potreba znat selectedRecord neupraven)
        const newActualRecordId = getRecordIdByDirection(
            recordsIds,
            state,
            getSelectionDirectionFromAction(action),
        );
        if (!newActualRecordId) {
            return state; // nelze se posunout (jsem na okraji DS).
        }
        return newActualRecordId;
    }
    return state;
};

const multiselectMode: Reducer<boolean> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.multiselectMode,
    action,
) => {
    if (action instanceof ISetDatasourceMultiselectModeAction) {
        return action.payload.multiselectMode;
    }
    return state;
};

const keepCheckedIncomingData: Reducer<boolean> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.keepCheckedIncomingData,
    action,
) => {
    if (action instanceof ICheckAllRecordsAction) {
        return action.payload.keepIncomingDataChecked;
    }
    return state;
};

type isCheckedAllReducerParams = {
    readonly recordsIds: readonly string[];
    readonly checkedRecords: TypedObject<boolean>;
};

const isCheckedAll: ParametricReducer<boolean, isCheckedAllReducerParams> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.isCheckedAll,
    action,
    isCheckAllReducerParams,
) => {
    if (action instanceof IToggleSelectedRecordCheckAction || action instanceof IToggleCheckOnSelectedRecordsAction) {
        const { recordsIds, checkedRecords } = isCheckAllReducerParams;

        const checkedRecordsIds = recordsIds.filter(
            (id) => checkedRecords[id],
        );

        return checkedRecordsIds.length === recordsIds.length;
    } else if (action instanceof IRefreshDataAction) {
        // i když mám vybrané vše, tak zruším zaškrtnutí
        return false;
    } else if (action instanceof ISetDatasourceMultiselectModeAction) {
        if (!action.payload.multiselectMode) {
            return false;
        }
    } else if (action instanceof ICheckAllRecordsAction) {
        return action.payload.keepIncomingDataChecked;
    } else if (action instanceof IToggleCheckAllRecordsAction) {
        return !state;
    }
    return state;
};

const rootNodes: Reducer<RootNodeTypedObject> = (
    state = DATA_SOURCE_INIT_STATE.multiselect.rootNodes,
    action,
) => {
    if (action instanceof ISetRootNodes) {
        return action.payload.nodes;
    }
    return state;
};

type MultiselectReducerParams = {
    readonly config: IDataSourceConfig; // Při načítání dat potřebuje vědět, zdali config není undefined.
    readonly recordsIds: ReadonlyArray<RecordIdType>; // Při načítání dat potřebuje vědět počet záznamů.
};

export const multiselectReducer: ParametricReducer<IDataSourceMultiselect, MultiselectReducerParams> = (
    state = DATA_SOURCE_INIT_STATE.multiselect,
    action,
    reducerParams,
) => {
    return produce(state, (draft) => {
        const { config: { recordIdKey }, recordsIds } = reducerParams;

        const rootNodesState = rootNodes(draft.rootNodes, action);
        const keepCheckedIncomingDataState = keepCheckedIncomingData(draft.keepCheckedIncomingData, action);
        const lastSelectedRecordIdState = lastSelectedRecordId(draft.lastSelectedRecordId, action, recordsIds);
        const selectedRecordsState = selectedRecords(draft.selectedRecords, action, {
            recordIdKey,
            recordsIds,
            lastSelectedRecordId: draft.lastSelectedRecordId,
        });
        const checkedRecordsState = checkedRecords(draft.checkedRecords, action, {
            recordIdKey,
            selectedRecords: selectedRecordsState,
            keepIncomingDataChecked: keepCheckedIncomingDataState,
            isCheckedAll: draft.isCheckedAll,
            rootNodes: rootNodesState,
        });
        const multiselectModeState = multiselectMode(draft.multiselectMode, action);
        const isCheckAllState = isCheckedAll(draft.isCheckedAll, action, { recordsIds, checkedRecords: checkedRecordsState });
        draft.selectedRecords = selectedRecordsState;
        draft.checkedRecords = checkedRecordsState;
        draft.lastSelectedRecordId = lastSelectedRecordIdState;
        draft.multiselectMode = multiselectModeState;
        draft.isCheckedAll = isCheckAllState;
        draft.keepCheckedIncomingData = keepCheckedIncomingDataState;
        draft.rootNodes = rootNodesState;
    });
};
